import { Injectable } from '@angular/core';

export interface RegistrationAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  msclkid?: string;
  ttclid?: string;
  liFatId?: string;
  sourcePage?: string;
  landingPage: string;
  signupUrl: string;
  referrer?: string;
  capturedAt: string;
  entryRoute: string;
  acquisitionFlow: 'paid_direct_registration';
}

const STORAGE_KEY = 'registrationAttribution';
const MAX_VALUE_LENGTH = 255;
const MAX_URL_LENGTH = 2048;

const QUERY_FIELD_MAP: Readonly<Record<string, keyof RegistrationAttribution>> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  fbclid: 'fbclid',
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  msclkid: 'msclkid',
  ttclid: 'ttclid',
  li_fat_id: 'liFatId',
  sourcePage: 'sourcePage'
};

@Injectable({ providedIn: 'root' })
export class RegistrationAttributionService {
  capture(entryRoute: string): RegistrationAttribution | null {
    const existing = this.get();
    if (existing) {
      return existing;
    }

    const url = new URL(window.location.href);
    const attribution: Partial<RegistrationAttribution> = {};
    let hasCampaignParameter = false;

    for (const [queryName, fieldName] of Object.entries(QUERY_FIELD_MAP)) {
      const value = this.normalizeValue(url.searchParams.get(queryName), fieldName === 'sourcePage' ? MAX_URL_LENGTH : MAX_VALUE_LENGTH);
      if (!value) {
        continue;
      }
      attribution[fieldName] = value as never;
      if (fieldName !== 'sourcePage') {
        hasCampaignParameter = true;
      }
    }

    if (!hasCampaignParameter) {
      return null;
    }

    const snapshot: RegistrationAttribution = {
      ...attribution,
      landingPage: this.normalizeValue(url.pathname, 512) || '/',
      signupUrl: this.buildSafeSignupUrl(url),
      referrer: this.normalizeReferrer(document.referrer),
      capturedAt: new Date().toISOString(),
      entryRoute,
      acquisitionFlow: 'paid_direct_registration'
    };

    if (!snapshot.referrer) {
      delete snapshot.referrer;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    return snapshot;
  }

  get(): RegistrationAttribution | null {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    try {
      const parsed: unknown = JSON.parse(stored);
      if (!this.isValidSnapshot(parsed)) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  clear(): void {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  private buildSafeSignupUrl(url: URL): string {
    const safeUrl = new URL(url.origin + url.pathname);
    for (const queryName of Object.keys(QUERY_FIELD_MAP)) {
      const value = url.searchParams.get(queryName);
      if (value) {
        safeUrl.searchParams.set(queryName, value);
      }
    }
    return safeUrl.toString().slice(0, MAX_URL_LENGTH);
  }

  private normalizeReferrer(value: string): string | undefined {
    if (!value) {
      return undefined;
    }
    try {
      const referrer = new URL(value);
      referrer.username = '';
      referrer.password = '';
      referrer.hash = '';
      return referrer.toString().slice(0, MAX_URL_LENGTH);
    } catch {
      return undefined;
    }
  }

  private normalizeValue(value: string | null, maxLength: number): string | undefined {
    const normalized = value?.trim().replace(/[\u0000-\u001F\u007F]/g, '');
    return normalized ? normalized.slice(0, maxLength) : undefined;
  }

  private isValidSnapshot(value: unknown): value is RegistrationAttribution {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const candidate = value as Partial<RegistrationAttribution>;
    return candidate.acquisitionFlow === 'paid_direct_registration'
      && typeof candidate.landingPage === 'string'
      && typeof candidate.signupUrl === 'string'
      && typeof candidate.capturedAt === 'string'
      && typeof candidate.entryRoute === 'string';
  }
}
